import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Skeleton, Grid } from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';
import {
	CerebroBreadcrumb,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroInput,
	CerebroPageContainer,
	CerebroSecondaryButton,
	CerebroText,
} from '../shared/components';
import { useAuth } from '../shared/context';
import { useDataRoomActivityLogQuery } from '../shared/hooks';
import { DateUtils, EnumUtils } from '../shared/utils';

export const ActivityLog: React.FC = () => {
	const { currentUser } = useAuth();
	const { guid } = useParams<'guid'>();
	const [searchName, setSearchName] = React.useState<string>('');
	const [page, setPage] = React.useState<number>(1);
	const searchRef = React.useRef<HTMLInputElement | null>(null);

	const onSearch = () => {
		setSearchName(searchRef.current?.value ?? '');
		setPage(1);
	};

	const logQuery = useDataRoomActivityLogQuery(guid, currentUser?.userGuid, searchName, page);
	const colSpan = currentUser.isBankUser ? 2 : 3;

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Activity Log</CerebroBreadcrumb>

			{!currentUser.isBankUser && (
				<Grid container>
					<Grid item xs={6}>
						<CerebroInput ref={searchRef} placeholder={'Search by name/email'} />
					</Grid>
					<Grid item xs={6}>
						<CerebroSecondaryButton onClick={() => onSearch()} sx={{ marginTop: '12px', marginLeft: 2 }}>
							Search
						</CerebroSecondaryButton>
					</Grid>
				</Grid>
			)}

			<TableContainer sx={{ marginBottom: 3 }}>
				<Table>
					<TableHead>
						<TableRow>
							{!currentUser.isBankUser && <TableCell>User</TableCell>}
							<TableCell>Action</TableCell>
							<TableCell>Date</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{logQuery.isFetching ||
							(logQuery.isLoading && (
								<>
									<TableRow>
										<TableCell colSpan={colSpan}>
											<Skeleton height={60} />
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell colSpan={colSpan}>
											<Skeleton height={60} />
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell colSpan={colSpan}>
											<Skeleton height={60} />
										</TableCell>
									</TableRow>
								</>
							))}
						{logQuery.isSuccess && !logQuery.isFetching && (
							<>
								{logQuery.data.logs.map(log => (
									<TableRow key={'log' + log.id}>
										{!currentUser.isBankUser && <TableCell>{log.performedByName}</TableCell>}
										<TableCell>
											{EnumUtils.GetStatObjectTypesDescription(log.statObjectType)}
										</TableCell>
										<TableCell>
											{DateUtils.FormatDateTimeStringWithAmPm(log.createdOn?.toString())}
										</TableCell>
									</TableRow>
								))}

								{logQuery.data.logs.length == 0 && (
									<TableRow>
										<TableCell colSpan={colSpan} sx={{ textAlign: 'center' }}>
											<CerebroText sx={{ paddingTop: 3, paddingBottom: 3 }}>
												There are currently no logs.
											</CerebroText>
										</TableCell>
									</TableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{logQuery.isSuccess && !logQuery.isFetching && logQuery.data.paging.totalPages > 1 && (
				<>
					{logQuery.data.paging.hasPreviousPage && (
						<CerebroSecondaryButton sx={{ marginRight: 2 }} onClick={() => setPage(page - 1)}>
							Previous
						</CerebroSecondaryButton>
					)}

					<CerebroDropdown
						sx={{ width: 200, marginRight: 2 }}
						value={page}
						onChange={e => setPage(typeof e.target.value === 'number' ? e.target.value : 1)}
					>
						{GetPagingMenuItems(logQuery.data.paging.totalPages, page)}
					</CerebroDropdown>

					{logQuery.data.paging.hasNextPage && (
						<CerebroSecondaryButton onClick={() => setPage(page + 1)}>Next</CerebroSecondaryButton>
					)}
				</>
			)}
		</CerebroPageContainer>
	);
};

function GetPagingMenuItems(totalPages: number, currentPage: number): JSX.Element[] {
	const items: JSX.Element[] = [] as JSX.Element[];

	for (let i = 1; i <= totalPages; i++) {
		items.push(
			<CerebroDropdownMenuItem key={i} value={i} selected={currentPage == i}>
				{i}
			</CerebroDropdownMenuItem>
		);
	}

	return items;
}
